import React, { createContext, useContext, useState } from 'react'
import axios from '../../api/axios-configure'
import { LoaderContext } from './LoaderContext'
import { toast } from 'react-toastify'
import format from 'date-fns/format'
import { Usertype } from 'utils/Constants'

const StatementContext = createContext(null)

const StatementsProvider = (props) => {
    const [isReceiver, setIsReceiver] = useState(null)
    const [isSender, setIsSender] = useState(null)
    const [roles, setRoles] = useState([])
    const [receiverStatements, setReceiverStatements] = useState(null)
    const [senderStatements, setSenderStatements] = useState(null)
    const [currentSenderCompanyName, setCurrentSenderCompanyName] = useState("")
    const [currentDocumentDescription, setCurrentDocumentDescription] = useState("")
    const [lastActivePage, setLastActivePage] = useState("Receiver")
    const [canUploadFileCurrentCompany, setCanUploadFileCurrentCompany] = useState(false)
    const [isFetchingReceiverSummary, setIsFetchingReceiverSummary] = useState(false)
    const [isFetchingSenderSummary, setIsFetchingSenderSummary] = useState(false)
    const [isShowHeaderButton, setIsShowHeaderButton] = useState(true)
    const { setIsLoading } = useContext(LoaderContext)

    const downloadSuccess = () => {
        toast.dismiss()
        toast.success('Your download has been successful!', {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: 'toastify__toast--success-custom',
            bodyClassName: 'toastify-body',
            progressClassName: 'toastify-progress-bar',
        })
    }

    const getUserPermissionsSummary = async () => {
        setIsLoading(true)
        axios.get('/GetUserPermissionsSummary')
        .then(response => {
            const { isReceiver, isSender, roles } = response.data
            setIsReceiver(isReceiver)
            setIsSender(isSender)
            setRoles(roles)
        })
        .catch(error => {
            console.log("error", error)
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const getSenderStatementsSummary = async () => {
        setIsFetchingSenderSummary(true)
        axios.get('/GetSenderStatementsSummary')
        .then(response => {
            setSenderStatements(response.data.senderSummaries)
        })
        .catch(error => {
            setSenderStatements([])
            console.log("error", error)
        }).finally(() => {
            setIsFetchingSenderSummary(false)
        })
    }
    
    const getReceiverStatementsSummary = () => {
        setIsFetchingReceiverSummary(true)
        axios.get('/GetReceiverStatementsSummary')
        .then(response => {
            setReceiverStatements(response.data.receiverSummaryList)
        })
        .catch(error => {
            setReceiverStatements([])
            console.log("error", error)
        }).finally(() => {
            setIsFetchingReceiverSummary(false)
        })
    }

    const getPayoutStatementPDF = async (data, type) => {
        var userType = 0
        if(type === Usertype.SENDER)
            userType = 1
        if(type === Usertype.RECEIVER)
            userType = 2

        axios.post(`/GetPayoutStatementPDF?userType=${userType}`, data)
        .then((response) => {
            if (response.data) {
                downloadSuccess()
                if (response.data.responseData.startsWith("JVB")) {
                    let pdfWindow = window.open()
                    pdfWindow.document.write(
                        "<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
                        encodeURI(response.data.responseData) + "'></iframe>"
                    );         
                    pdfWindow.document.title = response.data.fileName;

                    setTimeout(() => {
                        pdfWindow.stop()
                        window.dispatchEvent(new Event('PdfDownloadComplete'))
                    }, 1000)
                }
            }
        })
        .catch(error => {
            console.log("error", error);
        })
    }

    const getStatementStatus = (status) => {
       switch(status){
        case "Current": return 1; 
        case "Outdated": return 2;
        case "Past": return 3;
        case "Hidden": return 4;
        case "Unhidden": return 5;
       }
    }

    const getProductBalancingStatementPDF = async (data, type) => {
        var userType = 0
        if(type === Usertype.SENDER)
            userType = 1
        if(type === Usertype.RECEIVER)
            userType = 2

        axios.post(`/GetProductBalancingStatementPDF?userType=${userType}`, data)
        .then((response) => {
            if (response.data) {
                downloadSuccess()
                if (response.data.responseData.startsWith("JVB")) {
                    let pdfWindow = window.open()
                    pdfWindow.document.write(
                        "<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
                        encodeURI(response.data.responseData) + "'></iframe>"
                    );         
                    pdfWindow.document.title = response.data.fileName;

                    setTimeout(() => {
                        pdfWindow.stop()
                    }, 1000)
                }
            }
        })
        .catch(error => {
            console.log("error", error);
        })
    }

    const getProductImbalanceStatementCSV = async (data) => {
        axios.post("/CreateCSVInfoForStatements", data)
        .then((response) => {
            if (response.data) {
                downloadSuccess()
                const csvContent = "data:text/csv;charset=utf-8," + response.data;
                const encodedUri = encodeURI(csvContent).replaceAll('#', '%23');
                const link = document.createElement("a");
                link.setAttribute("href", encodedUri);
                const formattedDate = format(new Date(), "yyyyMMdd")
                const filename = `energilitydx-gas-balancing-${formattedDate}`;
                link.setAttribute("download", filename);
                document.body.appendChild(link); 
                link.click();
            }
        })
        .catch(error => {
            console.log("error", error);
        })
    }

    const cleanStore = () => {
        setIsReceiver(null)
        setIsSender(null)
        setReceiverStatements(null)
        setSenderStatements(null)
        setCurrentSenderCompanyName("")
        setCurrentDocumentDescription("")
        setLastActivePage("Receiver")
        setCanUploadFileCurrentCompany(false)
        setIsFetchingReceiverSummary(false)
        setIsFetchingSenderSummary(false)
    }

    return (
        <StatementContext.Provider
            value={{
                isReceiver,
                setIsReceiver,
                isSender,
                setIsSender,
                roles,
                receiverStatements,
                setReceiverStatements,
                senderStatements,
                setSenderStatements,
                getUserPermissionsSummary,
                getSenderStatementsSummary,
                getReceiverStatementsSummary,
                currentSenderCompanyName,
                setCurrentSenderCompanyName,
                currentDocumentDescription,
                setCurrentDocumentDescription,
                cleanStore,
                getProductBalancingStatementPDF,
                getStatementStatus,
                lastActivePage,
                setLastActivePage,
                getProductImbalanceStatementCSV,
                canUploadFileCurrentCompany,
                setCanUploadFileCurrentCompany,
                isFetchingReceiverSummary,
                isFetchingSenderSummary,
                isShowHeaderButton, 
                setIsShowHeaderButton,
                getPayoutStatementPDF
            }}
        >
            {props.children}
        </StatementContext.Provider>
    )
}

export { StatementsProvider, StatementContext }
